/**
 * Telegram's WebView (and mobile browsers in general) does NOT shrink
 * `100vh`/`h-screen` when the on-screen keyboard opens - the layout
 * viewport stays full height, so a fixed-height app shell just gets
 * covered by the keyboard instead of resizing above it.
 *
 * `window.visualViewport` DOES report the real visible height (it
 * shrinks when the keyboard opens). We mirror that into a CSS variable
 * every layout in the app can size itself against, and additionally
 * scroll whatever field the user just focused into view once the
 * keyboard has finished animating in.
 */
export function installKeyboardViewportFix(): void {
  const vv = window.visualViewport;

  const applyHeight = () => {
    const height = vv ? vv.height : window.innerHeight;
    document.documentElement.style.setProperty("--app-height", `${height}px`);
  };

  applyHeight();

  if (vv) {
    vv.addEventListener("resize", applyHeight);
    // iOS fires scroll on the visualViewport as the keyboard animates,
    // before the final resize settles - re-applying keeps things smooth.
    vv.addEventListener("scroll", applyHeight);
  } else {
    window.addEventListener("resize", applyHeight);
  }

  document.addEventListener(
    "focusin",
    (e) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const tag = target.tagName;
      if (tag !== "INPUT" && tag !== "TEXTAREA") return;

      // Wait for the keyboard's open animation (and the resulting
      // visualViewport resize) to actually finish before scrolling,
      // otherwise we scroll against the pre-keyboard layout.
      window.setTimeout(() => {
        target.scrollIntoView({ block: "center", behavior: "smooth" });
      }, 300);
    },
    true,
  );
}
